import * as React from 'react'

import { useVersionComparisonStore } from '../store'

// Use a WeakMap to track updating state per version ID (better than global variables)
const updatingVersions = new WeakMap<HTMLElement, boolean>()

/**
 * Applies scroll position to all layout containers except the source
 * Only applies to versions that are ready (isReady === true)
 * Uses requestAnimationFrame to batch DOM updates for better performance
 */
const applyScrollToAllLayouts = (
  sourceVersionId: string,
  scrollLeft: number,
  scrollTop: number,
  compareVersions: Array<{ id: string; isReady?: boolean }> | null,
  sourceElement: HTMLElement
) => {
  // Check if this source element is already updating (prevents infinite loop)
  if (updatingVersions.get(sourceElement)) return

  if (!compareVersions || compareVersions.length === 0) return

  // Mark this source element as updating
  updatingVersions.set(sourceElement, true)

  // Collect all containers that need to be updated
  const containersToUpdate: HTMLElement[] = []

  compareVersions.forEach((version) => {
    // Skip the source layout to avoid infinite loop
    if (version.id === sourceVersionId) return

    // Only apply scroll to ready versions
    if (!version.isReady) return

    // Find the scrollable container by version ID
    const scrollContainer = document.getElementById(version.id) as HTMLElement
    if (!scrollContainer) return

    // Check if this container is already updating
    if (updatingVersions.get(scrollContainer)) return

    // Only update if the scroll position is actually different
    if (
      scrollContainer.scrollLeft !== scrollLeft ||
      scrollContainer.scrollTop !== scrollTop
    ) {
      containersToUpdate.push(scrollContainer)
    }
  })

  // Batch all scroll updates in a single animation frame for better performance
  if (containersToUpdate.length > 0) {
    requestAnimationFrame(() => {
      // Apply all scroll updates synchronously in a single frame
      containersToUpdate.forEach((scrollContainer) => {
        // Mark target container as updating before setting scroll
        updatingVersions.set(scrollContainer, true)

        // Set scroll position (this will trigger scroll events, but they'll be ignored due to updatingVersions flag)
        scrollContainer.scrollLeft = scrollLeft
        scrollContainer.scrollTop = scrollTop
      })

      // Clean up flags after all updates are complete
      requestAnimationFrame(() => {
        containersToUpdate.forEach((scrollContainer) => {
          updatingVersions.delete(scrollContainer)
        })
        updatingVersions.delete(sourceElement)
      })
    })
  } else {
    // No containers to update, just clean up source flag
    requestAnimationFrame(() => {
      updatingVersions.delete(sourceElement)
    })
  }
}

/**
 * Hook to synchronize scrolling across all comparison layouts
 *
 * When any layout scrolls (x or y), all other layouts scroll by the same distance.
 * The scrollable container is identified by version ID (#versionId).
 *
 * @param versionId - The version ID of the current layout
 * @param scrollContainerRef - Ref to the scrollable container element
 * @param isReady - Whether the layout is ready to use scroll features
 */
export const useSynchronizedScroll = (
  versionId: string | undefined,
  scrollContainerRef: React.RefObject<HTMLDivElement | null>,
  isReady: boolean = false
) => {
  const { compareVersions } = useVersionComparisonStore()
  const rafIdRef = React.useRef<number | null>(null)
  const pendingScrollRef = React.useRef<{
    scrollLeft: number
    scrollTop: number
  } | null>(null)

  // Set up scroll event listener for this layout (only if ready)
  React.useEffect(() => {
    if (!isReady) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer || !versionId) return

    const applyPendingScroll = () => {
      if (!pendingScrollRef.current) return

      const { scrollLeft, scrollTop } = pendingScrollRef.current
      pendingScrollRef.current = null

      // Apply to all other layouts
      applyScrollToAllLayouts(
        versionId,
        scrollLeft,
        scrollTop,
        compareVersions,
        scrollContainer
      )
    }

    const handleScroll = (event: Event) => {
      const target = event.target as HTMLElement
      if (!target) return

      // Check if this scroll was triggered by our programmatic update
      if (updatingVersions.get(target)) return

      // Get current scroll position
      const scrollLeft = target.scrollLeft
      const scrollTop = target.scrollTop

      // Store pending scroll position (will be overwritten if new scroll comes in)
      pendingScrollRef.current = { scrollLeft, scrollTop }

      // Cancel any pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
      }

      // Schedule update for next animation frame (throttles to ~60fps)
      rafIdRef.current = requestAnimationFrame(applyPendingScroll)
    }

    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
      // Clean up pending animation frame
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = null
      }
      pendingScrollRef.current = null
    }
  }, [versionId, scrollContainerRef, compareVersions, isReady])
}
